import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { BissError, IBissSignedJson } from "@app/activation/services/ibiss";
import { catchError, map, switchMap } from 'rxjs/operators';
import { HeadersSetterHelper } from "@app/shared/helpers/http-headers-setter-helper";
import { Store } from "@ngrx/store";
import { observeTypes } from "@app/shared/interfaces/response-interfaces";
import { MatDialogRef } from "@angular/material/dialog";
import { AppConfig } from "@app/config/app.config";
import { InfoService } from "@app/core/info/info.service";
import { TranslateService } from "@ngx-translate/core";
import { HandleError, HttpErrorHandler } from "@app/shared/services/http-error-handler.service";
import { SignedContent, BissSignedContent, IBissLoginResponse, DownloadLinkData } from "../activation.model";
import { DeviceDetectorService } from "ngx-device-detector";
import { AngularDeviceInformationService } from "angular-device-information";
import { ActivationService } from "./activation.service";

@Injectable()
export class BissManager {
    private handleError: HandleError;
    private bissDialogRef: MatDialogRef<any>;
    private usedCert;

    deviceInfo = null;
    oSystem: string;
    isDesktopDevice: boolean;
    operatingSystem: string;
    downloadInfo: DownloadLinkData;
    constructor(
        public info: InfoService,
        private translate: TranslateService,
        private deviceService: DeviceDetectorService,
        private http: HttpClient,
        private httpErrorHandler: HttpErrorHandler,
        private headerHelper: HeadersSetterHelper,
        private translateService: TranslateService,

        private deviceInfoService: AngularDeviceInformationService,
        private activationService: ActivationService,

        private store: Store<any>
    ) {
        this.handleError = httpErrorHandler.createHandleError('BissManager');
        headerHelper = new HeadersSetterHelper(this.store)
        this.detectDevice()
    }

    changePIN(csp: string, cardSerialNumber: string): Observable<any> {
        const requestBody = { vendorName: csp, serNum: cardSerialNumber };
        return this.callBissSpecialFunction(requestBody, 'setPin');
    }

    changePUK(csp: string, cardSerialNumber: string): Observable<any> {

        const requestBody = { vendorName: csp, serNum: cardSerialNumber };

        return this.callBissSpecialFunction(requestBody, 'setPuk');

    }

    private callBissSpecialFunction(request: any, bissAdminFunctionName: string): Observable<any> {
        return this.createJWS(JSON.stringify(request))
            .pipe(
                switchMap(res => {
                    const url = 'https://localhost:53952/' + bissAdminFunctionName;
                    const requestData = {
                        jwsData: res.singedJsonObject
                    };

                    return this.http.post<any>(url, requestData).pipe(
                        map((bissResponse: any) => {
                            if (bissResponse.status === 200) {
                                return of(bissResponse);
                            } else {
                                throw new BissError(this.translate.instant('activation.documents.bissError'));
                            }
                        }),
                        catchError(e => this.handleBissError(e))
                    );
                }));
    }

    private createJWS(jsonData: string): Observable<IBissSignedJson> {
        const requestBody = { jsonObjectForSign: jsonData };
        const url = AppConfig.endpoints.activation.biss.bissAuth;
        return this.http.post(url, requestBody, this.headerHelper.options(observeTypes.BODY))
            .pipe(
                catchError(this.handleError('bissDigitallySignedMessage')),
                switchMap((response: any) => {
                    if (response.code === 'OK') {
                        return of(response);
                    } else {
                        this.info.error(this.translate.instant(response.message));
                        return of(null);
                    }
                }));
    }

    handleBissError(e: BissError | any): Observable<any> {
        const errMessage = e instanceof BissError ? e.message : e.error.reasonText;
        this.info.error(errMessage);
        return of(null);
    }

    writeCertToSc(requestData: any) {
        return this.callBissSpecialFunction(requestData, 'writecertsc');
    }

    getSigner(): Observable<string> {
        const getSignerUrl = 'https://localhost:53952' + '/getsigner';
        const body = {
            selector: {},
            showValidCerts: true
        };
        return this.http.post(getSignerUrl, body).pipe(switchMap((response: any) => {
            if (response.reasonCode === 200) {
                this.usedCert = response.chain[0];
                return of(response.chain[0]);
            }
            if (response.reasonCode !== 200) {
                throw new BissError(this.translate.instant('biss.rejectedByClient'));
            }
        }),
            catchError(e => this.handleBissError(e))
        );
    }

    bissSign(result: SignedContent[]): Observable<BissSignedContent[]> {
        const url = 'https://localhost:53952' + '/sign';

        const contentsArr: string[] = [];
        const signedContentsArr: string[] = [];
        const signedContentsCertArr: string[] = [];

        result.forEach(c => {
            contentsArr.push(c.content);
            signedContentsArr.push(c.signedContent);
            signedContentsCertArr.push(c.signedContentCert);
        });

        const objToSignWithBiss = {
            version: '2.16',
            contents: contentsArr,
            signedContents: signedContentsArr,
            signedContentsCert: signedContentsCertArr,
            contentType: 'digest',
            hashAlgorithm: 'SHA256',
            signatureType: 'signature',
            signerCertificateB64: this.usedCert,
            confirmText: null
        };

        return this.http.post(url, objToSignWithBiss).pipe(
            switchMap((bissResponse: IBissLoginResponse) => {
                if (bissResponse.reasonCode === 200) {
                    const bissSignedContents: BissSignedContent[] = [];
                    for (let i = 0; i < bissResponse.signatures.length; i++) {
                        bissSignedContents.push(
                            new BissSignedContent(
                                contentsArr[i],
                                bissResponse.signatures[i],
                                this.usedCert,
                                result[i].originalContent,
                                result[i].digestTime,
                                result[i].filename,
                                result[i].type));
                    }
                    return of(bissSignedContents);
                } else {

                    throw new BissError(this.translate.instant('biss.signingError'));
                }
            }),

            catchError(e => this.handleBissError(e))
        );
    }

    checkSmartCard(): Observable<any> {
        const request = {
            vendor: '',
            pattern: ''
        }
        return this.callBissSpecialFunction(request, 'getserialnumber')
    }

    detectDevice() {
        this.deviceInfo = this.deviceService.getDeviceInfo();
        this.oSystem = this.deviceService.os;
        this.isDesktopDevice = this.deviceService.isDesktop();
    }
    getDownloadInfoLink() {
        this.operatingSystem = this.deviceInfoService.isDesktop() ? this.deviceInfoService.getDeviceInfo().os : 'WINDOWS';
        if (this.operatingSystem) {
            this.activationService.getDownloadInfo(this.mapOperatingSystem(this.operatingSystem)).subscribe((res: any) => {
                if (res.status === 200) {
                    this.downloadInfo = res.data;
                }
            })
        } else {
            this.info.error(this.translateService.instant('activation.biss.osNotSupported'));
        }
    }
    mapOperatingSystem(os: string) {
        if (os.toLowerCase().includes('mac')) {
            return 'MAC';
        }
        return os;
    }
    public getBissDownloadUrl(): string {
        return this.downloadInfo.bissDownloadLink   
    }

    public getUsedCert() {
        return this.usedCert;
    }

    public setUsedCert(certificate: string) {
        this.usedCert = certificate;
    }
}